import React, { useState, forwardRef } from 'react';
import { TextField, InputAdornment, Box } from '@mui/material';
import { motion } from 'framer-motion';
import Visibility from '../../../../assets/images/eye.svg';
import VisibilityOff from '../../../../assets/images/eye-off.svg';
import CustomIconButton from '../IconButton/CustomIconButton';
import { validateData, type ErrorObject } from '../../providers/validateData';
import CustomTypography from '../Typography/CustomTypography';

interface CustomTextFieldProps extends ValidationError {
  id: string;
  label?: string;
  value?: string | number;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  variant?: 'outlined' | 'filled' | 'standard';
  className?: string;
  type?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  defaultValue?: string;
  required?: boolean;
  children?: React.ReactNode;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  isPassword?: boolean;
  isAnimated?: boolean;
  validationMessages?: Partial<Record<string, string>>;
  multiLine?: boolean;
  rows?: number;
  onlyDigits?: boolean; // NEW prop to allow digits-only input
}

interface ValidationError {
  onValidationError?: (error: ErrorObject | null) => void;
  validateRules?: (
    | 'required'
    | 'email'
    | 'phone'
    | 'password'
    | 'minLength'
    | 'maxLength'
  )[];
  minLength?: number;
  maxLength?: number;
}

const CustomTextField = forwardRef<HTMLInputElement, CustomTextFieldProps>(
  (
    {
      id,
      label,
      value = '',
      name,
      onChange,
      onClick,
      onBlur,
      onKeyDown: onKeyDownProp,
      color,
      variant = 'outlined',
      className,
      type = 'text',
      placeholder,
      size = 'medium',
      disabled = false,
      fullWidth = true,
      error = false,
      helperText,
      autoComplete = 'off',
      autoFocus = false,
      defaultValue,
      required = false,
      children,
      startAdornment,
      endAdornment,
      isPassword = false,
      isAnimated = false,
      validateRules,
      minLength,
      maxLength,
      onValidationError,
      validationMessages,
      multiLine = false,
      rows = 4,
      onlyDigits = false,
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [inputError, setInputError] = useState<ErrorObject | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = event.target.value;

      if (event.target.type === 'number') {
        newValue = newValue.replace(/[+\-e]/gi, '');
      }

      // Restrict input if onlyDigits is true
      if (onlyDigits && !/^\d*$/.test(newValue)) return;

      if (onChange) {
        onChange(event);
      }

      if (validateRules) {
        const errors = validateData(
          newValue,
          validateRules,
          minLength,
          maxLength,
          validationMessages
        );
        if (onValidationError) {
          onValidationError(errors);
        }
        setInputError(errors);
      }
    };

    const getShowPwdIcon = (showPassword: boolean) =>
      showPassword ? <Visibility /> : <VisibilityOff />;

    const effectiveEndAdornment = isPassword ? (
      <CustomIconButton
        id={`${id}-toggle-password`}
        onClick={togglePasswordVisibility}
        size={size}
        icon={getShowPwdIcon(showPassword)}
      />
    ) : (
      endAdornment
    );

    const handleMouseEnter = () => {
      if (isAnimated) setIsExpanded(true);
    };

    const handleMouseLeave = () => {
      if (isAnimated && !isFocused && !value) setIsExpanded(false);
    };

    const handleFocus = () => {
      if (isAnimated) {
        setIsExpanded(true);
        setIsFocused(true);
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (isAnimated && !value) setIsExpanded(false);
    };

    const getAnimateWidth = (isExpanded: boolean) => {
      if (isExpanded) return 250;
      return 58;
    };

    let animateWidth;
    if (isAnimated) {
      animateWidth = getAnimateWidth(isExpanded);
    } else {
      animateWidth = '100%';
    }

    let initialWidth;
    if (isAnimated) {
      initialWidth = 58;
    } else {
      initialWidth = '100%';
    }

    const getType = (showPassword: boolean) => {
      if (showPassword) return 'text';
      return 'password';
    };

    let inputType;
    if (isPassword) {
      inputType = getType(showPassword);
    } else {
      inputType = type;
    }

    return (
      <Box
        component={motion.div}
        initial={{ width: initialWidth }}
        animate={{ width: animateWidth }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <TextField
          id={id}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (
              event.currentTarget.type === 'number' &&
              ['e', 'E', '+', '-'].includes(event.key)
            ) {
              event.preventDefault();
            }
            event.stopPropagation();
            onKeyDownProp?.(event);
          }}
          label={label}
          name={name}
          value={value}
          color={color}
          onChange={handleOnChange}
          onClick={(event) => {
            isAnimated && setIsExpanded(true);
            onClick?.(event);
          }}
          className={className}
          variant={variant}
          type={inputType}
          placeholder={placeholder}
          size={size}
          disabled={disabled}
          fullWidth={fullWidth}
          error={error}
          multiline={multiLine}
          rows={rows}
          helperText={helperText}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          defaultValue={defaultValue}
          required={required}
          inputRef={ref}
          onFocus={() => isAnimated && setIsExpanded(true)}
          onBlur={(event) => {
            if (isAnimated && !value) {
              setIsExpanded(false);
            }
            onBlur?.(event);
          }}
          slotProps={{
            input: {
              inputMode: onlyDigits ? 'numeric' : undefined,
              startAdornment: startAdornment ? (
                <InputAdornment position="start">
                  {startAdornment}
                </InputAdornment>
              ) : undefined,
              endAdornment: effectiveEndAdornment ? (
                <InputAdornment position="end">
                  {effectiveEndAdornment}
                </InputAdornment>
              ) : undefined,
            },
            htmlInput: {
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                const actualType = (e.target as HTMLInputElement).type;

                if (
                  actualType === 'number' &&
                  ['e', 'E', '+', '-'].includes(e.key)
                ) {
                  e.preventDefault();
                }
              },
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              ...(isAnimated && {
                border: '1px solid #E6ECF1 !important',
                backgroundColor: '#E6ECF1 !important',
                transition: 'all 0.3s ease-in-out',
              }),
              ...(disabled && {
                backgroundColor: '#d4d4d4', // Background color when disabled
              }),
            },

            '&:hover': {
              ...(isAnimated && {
                backgroundColor: '#E6ECF1 !important',
                borderRadius: '24px',
              }),
            },
            '&.Mui-focused': {
              ...(isAnimated && {
                backgroundColor: '#E6ECF1 !important',
                borderRadius: '24px',
              }),
            },

            input: {
              '&::placeholder': {
                ...(isAnimated && {
                  color: '#848B90',
                  fontSize: '12px',
                  fontWeight: '500',
                }),
              },
            },
          }}
        >
          {children}
        </TextField>

        {inputError?.invalid && (
          <CustomTypography id="id-error" variant="h6" color="error">
            {inputError?.message}
          </CustomTypography>
        )}
      </Box>
    );
  }
);

CustomTextField.displayName = 'CustomTextField';

export default CustomTextField;
